import Swiper from 'swiper'
import type { SwiperOptions } from 'swiper/types'
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/effect-fade'

Swiper.use([Navigation, Pagination, Autoplay, EffectFade]);

/**
 * Get the swiper variant from element classes
 */
function getSwiperVariant(element: HTMLElement): string | null {
  const classes = Array.from(element.classList);
  const variantClass = classes.find(cls => cls.startsWith('swiper--'));
  return variantClass ? variantClass.replace('swiper--', '') : null;
}

/**
 * Get navigation button selectors from data attributes
 */
function getNavigationSelectors(element: HTMLElement): { prev: string | null; next: string | null } {
  return {
    prev: element.dataset.swiperPrev || null,
    next: element.dataset.swiperNext || null,
  };
}

/**
 * Get slide count from element
 */
function getSlideCount(element: HTMLElement): number {
  return element.querySelectorAll('.swiper-slide').length;
}

/**
 * Setup click vs drag detection for carousel links
 */
// function setupCarouselDragDetection(element: HTMLElement): void {
//   let dragStart = false;
//   let clickStart = { x: 0, y: 0 };

//   element.addEventListener('mousedown', (e) => {
//     dragStart = true;
//     clickStart = { x: e.clientX, y: e.clientY };
//   });

//   element.addEventListener('mouseup', (e) => {
//     const clickEnd = { x: e.clientX, y: e.clientY };
//     const distance = Math.sqrt(
//       Math.pow(clickEnd.x - clickStart.x, 2) + Math.pow(clickEnd.y - clickStart.y, 2)
//     );

//     // If moved more than 5px, it's a drag - prevent link navigation
//     if (dragStart && distance > 5) {
//       const link = (e.target as HTMLElement).closest('.swiper-slide-link') as HTMLAnchorElement;
//       if (link) {
//         e.preventDefault();
//       }
//     }
//     dragStart = false;
//   });
// }

/**
 * Get swiper configuration based on variant
 */
function getVariantConfig(variant: string, element: HTMLElement): SwiperOptions | null {
  const slideCount = getSlideCount(element);
  const navigation = getNavigationSelectors(element);

  switch (variant) {
    case 'hero': {
      // Hero slider - fade or slide transition
      if (slideCount <= 1) return null;

      const effect = element.dataset.swiperEffect || 'slide';
      const config: SwiperOptions = {
        modules: effect === 'fade' ? [Navigation, EffectFade] : [Navigation],
        slidesPerView: 1,
        allowTouchMove: false,
        autoHeight: true,
        loop: slideCount > 1,
        speed: 600,
        navigation: navigation.prev && navigation.next ? {
          prevEl: navigation.prev,
          nextEl: navigation.next,
        } : false,
      };

      if (effect === 'fade') {
        config.effect = 'fade';
        config.fadeEffect = {
          crossFade: true,
        };
      }

      return config;
    }

    case 'gallery': {
      // Image gallery slider with pagination and navigation
      const paginationSelector = element.dataset.swiperPagination || null;
      
      return {
        modules: [Navigation, Pagination],
        loop: true,
        slidesPerView: 1,
        spaceBetween: 0,
        navigation: navigation.prev && navigation.next ? {
          prevEl: navigation.prev,
          nextEl: navigation.next,
        } : false,
        pagination: paginationSelector ? {
          el: paginationSelector,
          type: 'bullets',
          clickable: true,
        } : false,
      };
    }

    case 'carousel': {
      // Content carousel with scale effect
      // setupCarouselDragDetection(element);

      const paginationSelector = element.dataset.swiperPagination || null;

      const config: SwiperOptions = {
        modules: paginationSelector ? [Pagination] : [],
        slidesPerView: 'auto',
        slidesPerGroup: 1,
        spaceBetween: 20,
        breakpoints: {
          576: { spaceBetween: 30 },
          768: { spaceBetween: 40 },
          992: { spaceBetween: 50 },
        },
        rewind: true,
        observer: true,
        centeredSlides: true,
        watchSlidesProgress: true,
        pagination: paginationSelector ? {
          el: paginationSelector,
          type: 'bullets',
          clickable: true,
        } : false,
        on: {
          init: function(swiper) {
            // Setup custom navigation handlers because swiper's built-in
            // navigation doesn't work well with centeredSlides and auto slidesPerView
            if (navigation.prev && navigation.next) {
              const prevBtn = document.querySelector(navigation.prev);
              const nextBtn = document.querySelector(navigation.next);
              
              if (prevBtn) {
                prevBtn.addEventListener('click', (e) => {
                  e.preventDefault();
                  console.log('Prev button clicked - moving to previous slide');
                  swiper.slidePrev();
                });
              }
              
              if (nextBtn) {
                nextBtn.addEventListener('click', (e) => {
                  e.preventDefault();
                  console.log('Next button clicked - moving to next slide');
                  swiper.slideNext();
                });
              }
            }
          },
          slideChange: function(swiper) {
            console.log('Slide changed - Active index:', swiper.activeIndex, 'Real index:', swiper.realIndex);
          },
          progress: function(swiper) {
            // Dynamic scaling during drag based on slide position
            for (let i = 0; i < swiper.slides.length; i++) {
              const slide = swiper.slides[i] as HTMLElement;
              const slideProgress = (slide as any).progress;
              const absProgress = Math.abs(slideProgress);

              // Scale from 0.75 to 1 based on distance from center
              const scale = 1 - Math.min(absProgress, 1) * 0.25;

              const slideInner = slide.querySelector('div, a') as HTMLElement;
              if (slideInner) {
                slideInner.style.transform = `scale(${scale})`;
              }
            }
          },
          setTransition: function(swiper, transition) {
            for (let i = 0; i < swiper.slides.length; i++) {
              const slide = swiper.slides[i] as HTMLElement;
              const slideInner = slide.querySelector('div, a') as HTMLElement;
              if (slideInner) {
                slideInner.style.transition = `transform ${transition}ms ease`;
              }
            }
          },
        },
      };

      return config;
    }

    case 'marquee': {
      // Logo slider with autoplay
      return {
        modules: [Autoplay],
        slidesPerView: 1,
        breakpoints: {
          576: { slidesPerView: 2 },
          768: { slidesPerView: 3 },
          992: { slidesPerView: 4 },
          1200: { slidesPerView: 5 },
          1400: { slidesPerView: 5 },
        },
        loop: slideCount > 1,
        spaceBetween: 48,
        speed: 8000,
        autoplay: {
          delay: 0,
          disableOnInteraction: false,
        },
      };
    }

    case 'testimonial': {
      // Testimonial slider
      if (slideCount <= 1) return null;

      return {
        modules: [Navigation],
        slidesPerView: 1,
        loop: slideCount >= 3,
        navigation: navigation.prev && navigation.next ? {
          prevEl: navigation.prev,
          nextEl: navigation.next,
        } : false,
      };
    }

    default:
      console.warn(`Unknown swiper variant: ${variant}`);
      return null;
  }
}

/**
 * Initialize all swiper instances on the page
 */
export function initSwipers(): void {
  const swipers = document.querySelectorAll('.swiper') as NodeListOf<HTMLElement>;

  swipers.forEach((swiperEl) => {
    // Skip if already initialized - check for swiper instance
    if ((swiperEl as any).swiper) {
      console.log('Swiper already initialized, skipping:', swiperEl);
      return;
    }

    const variant = getSwiperVariant(swiperEl);
    
    if (!variant) {
      console.warn('Swiper element found without variant class:', swiperEl);
      return;
    }

    const config = getVariantConfig(variant, swiperEl);
    
    if (!config) {
      // Config is null when conditions aren't met (e.g., single slide)
      return;
    }

    console.log('Initializing swiper:', variant, swiperEl);
    new Swiper(swiperEl, config);
  });
}
